import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Render a divider above this item. */
  separator?: boolean;
}

interface Props {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * A small right-click menu rendered in a portal at (x, y). Closes on outside
 * click, Escape, scroll, or resize. Clamps itself to stay within the viewport.
 */
export function ContextMenu({ x, y, items, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const nx = Math.min(x, window.innerWidth - width - 8);
    const ny = Math.min(y, window.innerHeight - height - 8);
    setPos({ x: Math.max(8, nx), y: Math.max(8, ny) });
  }, [x, y]);

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    // Capture phase so a click anywhere (including other rows) closes first.
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      // Own mousedown must not bubble to the document-level close handler.
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      style={{ top: pos.y, left: pos.x }}
      className="fixed z-[60] min-w-[190px] py-1 bg-[var(--color-bg-surface)] border border-[var(--color-border)] shadow-lg text-sm"
    >
      {items.map((item, i) => (
        <div key={i}>
          {item.separator && (
            <div className="my-1 border-t border-[var(--color-border)]" />
          )}
          <button
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.onClick();
            }}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left disabled:opacity-40 disabled:cursor-not-allowed ${
              item.danger
                ? "text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
                : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-base)]"
            }`}
          >
            <span className="flex-shrink-0 text-[var(--color-text-muted)]">
              {item.icon}
            </span>
            {item.label}
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
